import React, { useState, useEffect, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useLocation } from 'react-router-dom'
import { EditorViewport } from './EditorViewport'
import { MockExpressChrome } from './MockExpressChrome'
import { LinkPanelList } from './LinkPanelList'
import { FormulaModal } from './FormulaModal'
import { executeFormula } from './formulaUtils'
import { getSummaryBoxesUrl, saveSummaryLayoutCalibration, fetchVotingSummarySamples } from './editorApi'
import { LiveSvgExportModal } from './LiveSvgExportModal'
import { loadLinkPanelsFromLocal, saveLinkPanelsToLocal, clearLinkPanelsLocal } from './editorLocalStorage'
import { applyDynamicVoteThresholdsToPanels } from './applyDynamicVoteThresholds'

const DEFAULT_IMAGE_DIMS = { width: 1422, height: 1866 }
const DEBOUNCE_MS = 800

const PREGEN_IMPORTS = {
  'design-a': () => import('./pregenerated/design-a.json'),
  'design-b': () => import('./pregenerated/design-b.json'),
  'design-c': () => import('./pregenerated/design-c.json')
}

function getAutoUrl () {
  return window.location.origin + window.location.pathname + '#/website/summary'
}

function getLocalDateKey (date) {
  const d = date || new Date()
  return d.toLocaleDateString('en-CA')
}

function isValidSelection (sel) {
  return (
    sel &&
    typeof sel.left === 'number' &&
    typeof sel.top === 'number' &&
    typeof sel.width === 'number' &&
    typeof sel.height === 'number' &&
    sel.width > 0 &&
    sel.height > 0
  )
}

/** If todayKey has no valid box, copy the first valid selection from another date (pregenerated data). */
function copySelectionsToTodayKey (selectionsByDate, todayKey) {
  const src = selectionsByDate && typeof selectionsByDate === 'object' ? { ...selectionsByDate } : {}
  if (isValidSelection(src[todayKey])) return src
  for (const k of Object.keys(src)) {
    if (k === todayKey) continue
    const s = src[k]
    if (isValidSelection(s)) {
      src[todayKey] = { left: s.left, top: s.top, width: s.width, height: s.height }
      break
    }
  }
  return src
}

function randomColor () {
  return `#${Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0')}`
}

function withoutReasoning (panel) {
  if (panel == null || typeof panel !== 'object') return panel
  const { reasoning, ...rest } = panel
  return rest
}

/** Same text update as after a selection-box commit: directText + text (or formula output). */
function applyExtractedTextToPanel (panel, extractedText) {
  const updatedPanel = { ...panel, directText: extractedText }
  if (panel.type === 'formulaic' && panel.formula) {
    try {
      const parsed = JSON.parse(panel.formula)
      updatedPanel.text = executeFormula(extractedText, parsed.template || '', parsed.formula || '')
    } catch (_) {
      updatedPanel.text = extractedText
    }
  } else {
    updatedPanel.text = extractedText
  }
  return updatedPanel
}

/** Refresh panel directText/text from voting-summary-boxes (same geometry as live SVG). */
async function mergePanelsWithVotingApi (panels, imageDims, dateKey) {
  const vw = imageDims.width
  const vh = imageDims.height
  if (!panels.length || vw <= 0 || vh <= 0) return null
  const boxes = []
  for (let i = 0; i < panels.length; i++) {
    const sel = panels[i].selectionsByDate && panels[i].selectionsByDate[dateKey]
    if (!isValidSelection(sel)) continue
    boxes.push({ i, left: sel.left, top: sel.top, width: sel.width, height: sel.height })
  }
  if (!boxes.length) return null
  const data = await fetchVotingSummarySamples(
    { width: vw, height: vh },
    boxes
  )
  const samples = data && data.samples
  if (!Array.isArray(samples)) return null
  const byI = new Map(samples.map((s) => [Number(s.i), s.text]))
  let any = false
  const merged = panels.map((p, idx) => {
    const t = byI.get(idx)
    if (t == null || String(t) === '') return p
    any = true
    return applyExtractedTextToPanel(p, String(t))
  })
  return any ? merged : null
}

/** Sample the preview iframe at each panel's selection (like committing every box). */
function mergePanelsWithViewportText (viewportApi, panels, dateKey) {
  if (!viewportApi || typeof viewportApi.getTextAtSelection !== 'function') return panels
  return panels.map((panel) => {
    const sel = panel.selectionsByDate && panel.selectionsByDate[dateKey]
    if (!isValidSelection(sel)) return panel
    let extractedText = ''
    try {
      extractedText = viewportApi.getTextAtSelection(sel) || ''
    } catch (_) {}
    if (!extractedText) return panel
    return applyExtractedTextToPanel(panel, extractedText)
  })
}

function applyPanelsToSvgText (svgText, panels) {
  const parser = new DOMParser()
  const doc = parser.parseFromString(svgText, 'image/svg+xml')

  function getVisualElements (el) {
    const tags = ['path', 'rect', 'circle', 'ellipse', 'polygon', 'line', 'polyline']
    if (tags.includes(el.tagName.toLowerCase())) return [el]
    const found = []
    tags.forEach((t) => el.querySelectorAll(t).forEach((e) => found.push(e)))
    return found.length > 0 ? found : [el]
  }

  function applyProperty (el, propertyType, value) {
    if (!value) return
    switch (propertyType) {
      case 'fill-color':
        getVisualElements(el).forEach((e) => e.setAttribute('fill', value))
        break
      case 'border-color':
        getVisualElements(el).forEach((e) => e.setAttribute('stroke', value))
        break
      case 'text-color': {
        const tels = el.querySelectorAll('text, tspan')
        if (tels.length > 0) tels.forEach((e) => e.setAttribute('fill', value))
        else if (el.tagName === 'text' || el.tagName === 'tspan') el.setAttribute('fill', value)
        break
      }
      case 'opacity':
        el.setAttribute('opacity', value)
        break
      case 'border-thickness':
        getVisualElements(el).forEach((e) => e.setAttribute('stroke-width', value))
        break
      case 'text':
        if (el.tagName === 'text' || el.tagName === 'tspan') {
          el.textContent = value
        } else {
          const tts = el.querySelectorAll('text, tspan')
          if (tts.length > 0) tts[0].textContent = value
        }
        break
      default:
        break
    }
  }

  panels.forEach((panel) => {
    if (!panel.linkedNodeIds || !panel.propertyType || panel.propertyType === 'none') return
    ;(Array.isArray(panel.linkedNodeIds) ? panel.linkedNodeIds : Array.from(panel.linkedNodeIds)).forEach((nodeId) => {
      const el = doc.getElementById(nodeId)
      if (!el) return
      applyProperty(el, panel.propertyType, panel.text)
    })
  })

  return new XMLSerializer().serializeToString(doc)
}

function buildLiveExportPanels (linkPanels, dateKey) {
  return linkPanels.map((p, i) => {
    const sel = p.selectionsByDate && p.selectionsByDate[dateKey]
    const hasBox = isValidSelection(sel)
    return {
      i,
      left: hasBox ? sel.left : 0,
      top: hasBox ? sel.top : 0,
      width: hasBox ? sel.width : 0,
      height: hasBox ? sel.height : 0,
      linkedNodeIds: Array.isArray(p.linkedNodeIds) ? p.linkedNodeIds : [],
      propertyType: p.propertyType || 'none',
      type: p.type || 'direct',
      formula: p.formula || '',
      text: p.text || ''
    }
  })
}

function generateSummaryLiveScript ({ apiUrl, pollSeconds, viewport, panels }) {
  const pollMs = Math.max(5, Number(pollSeconds) || 5) * 1000
  return `
  <script type="text/javascript">
  <![CDATA[
    (function() {
      var API_URL = ${JSON.stringify(apiUrl)};
      var POLL_MS = ${pollMs};
      var VIEWPORT = ${JSON.stringify(viewport)};
      var PANELS = ${JSON.stringify(panels)};

      function executeFormula(selectionValue, template, formula) {
        if (!template && !formula) return selectionValue;
        try {
          var result = selectionValue;
          if (formula) {
            try {
              var safeContext = {
                value: selectionValue,
                num: (function() {
                  var m = String(selectionValue).match(/-?\\d+(\\.\\d+)?/);
                  return m ? parseFloat(m[0]) : 0;
                })(),
                Math: {
                  round: Math.round, floor: Math.floor, ceil: Math.ceil, abs: Math.abs,
                  min: Math.min, max: Math.max, pow: Math.pow, sqrt: Math.sqrt
                },
                upper: function(str) { return String(str).toUpperCase(); },
                lower: function(str) { return String(str).toLowerCase(); },
                parseFloat: parseFloat, parseInt: parseInt
              };
              var processedFormula = formula;
              processedFormula = processedFormula.replace(
                /\\bround\\(([^,)]+),\\s*(\\d+)\\)/g,
                'Math.round(($1) * Math.pow(10, $2)) / Math.pow(10, $2)'
              );
              processedFormula = processedFormula.replace(/\\bround\\(([^)]+)\\)/g, 'Math.round($1)');
              var keys = Object.keys(safeContext);
              var fn = new Function(keys.join(', '), '"use strict"; return (' + processedFormula + ');');
              result = String(fn.apply(null, keys.map(function(k) { return safeContext[k]; })));
            } catch (err) {
              result = 'Formula error: ' + err.message;
            }
          }
          if (template) {
            return template
              .replace(/\\bvalue\\b/g, selectionValue)
              .replace(/\\bresult\\b/g, result);
          }
          return result;
        } catch (_) {
          return 'Preview error';
        }
      }

      function getVisualElements(element) {
        var tags = ['path','rect','circle','ellipse','polygon','line','polyline'];
        if (tags.indexOf(element.tagName.toLowerCase()) >= 0) return [element];
        var els = [];
        tags.forEach(function(t) {
          element.querySelectorAll(t).forEach(function(e) { els.push(e); });
        });
        return els.length > 0 ? els : [element];
      }

      function applyProperty(element, propertyType, value) {
        if (!value) return;
        switch(propertyType) {
          case 'fill-color':
            getVisualElements(element).forEach(function(e) { e.setAttribute('fill', value); });
            break;
          case 'border-color':
            getVisualElements(element).forEach(function(e) { e.setAttribute('stroke', value); });
            break;
          case 'text-color':
            var tels = element.querySelectorAll('text, tspan');
            if (tels.length > 0) tels.forEach(function(e) { e.setAttribute('fill', value); });
            else if (element.tagName === 'text' || element.tagName === 'tspan') element.setAttribute('fill', value);
            break;
          case 'opacity':
            element.setAttribute('opacity', value);
            break;
          case 'border-thickness':
            getVisualElements(element).forEach(function(e) { e.setAttribute('stroke-width', value); });
            break;
          case 'text':
            if (element.tagName === 'text' || element.tagName === 'tspan') {
              element.textContent = value;
            } else {
              var tts = element.querySelectorAll('text, tspan');
              if (tts.length > 0) tts[0].textContent = value;
            }
            break;
        }
      }

      function applyPanelOutput(panel, displayValue) {
        var ids = panel.linkedNodeIds;
        if (!ids || !ids.length) return;
        ids.forEach(function(nodeId) {
          var el = document.getElementById(nodeId);
          if (!el) return;
          applyProperty(el, panel.propertyType, displayValue);
        });
      }

      function buildRequestBody() {
        return JSON.stringify({
          viewport: VIEWPORT,
          boxes: PANELS.map(function(p) {
            return { i: p.i, left: p.left, top: p.top, width: p.width, height: p.height };
          })
        });
      }

      function computeDisplayText(panel, raw) {
        var text = raw == null ? '' : String(raw);
        if (panel.type === 'formulaic' && panel.formula) {
          try {
            var parsed = JSON.parse(panel.formula);
            return executeFormula(text, parsed.template || '', parsed.formula || '');
          } catch (e) { return text; }
        }
        return text;
      }

      function paintFromPanels() {
        PANELS.forEach(function(p) {
          if (!p.text) return;
          applyPanelOutput(p, p.text);
        });
      }

      async function fetchAndApply() {
        try {
          var res = await fetch(API_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: buildRequestBody()
          });
          if (!res.ok) return;
          var data = await res.json();
          var samples = data.samples;
          if (!samples || !Array.isArray(samples)) return;
          var map = {};
          samples.forEach(function(s) { map[s.i] = s.text; });
          PANELS.forEach(function(p) {
            var raw = Object.prototype.hasOwnProperty.call(map, p.i) ? map[p.i] : '';
            var display = computeDisplayText(p, raw);
            applyPanelOutput(p, display);
          });
        } catch (e) {
          console.error('Live SVG summary poll failed:', e);
        }
      }

      function boot() {
        paintFromPanels();
        if (!PANELS.length) return;
        fetchAndApply();
        setInterval(fetchAndApply, POLL_MS);
      }

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot);
      } else {
        boot();
      }
    })();
  ]]>
  </script>
  `
}

function stripExistingScript (svgText) {
  return svgText.replace(/<script[\s\S]*?<\/script>/gi, '')
}

function injectScriptIntoSVG (svgText, script) {
  const insertPos = svgText.lastIndexOf('</svg>')
  if (insertPos === -1) throw new Error('Invalid SVG: no closing </svg> tag')
  return svgText.substring(0, insertPos) + script + svgText.substring(insertPos)
}

function downloadSVG (svgText, filename) {
  const blob = new Blob([svgText], { type: 'image/svg+xml' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export function LivingLinkAddon ({ canvas, ims }) {
  const location = useLocation()
  const autoUrl = getAutoUrl()
  const currentDateKey = getLocalDateKey()

  const [linkPanels, setLinkPanels] = useState([])
  const [loading, setLoading] = useState(true)
  const [recommendationsLoading, setRecommendationsLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [calibratingLayout, setCalibratingLayout] = useState(false)
  const [zoom, setZoom] = useState(1)
  const [imageDims, setImageDims] = useState(DEFAULT_IMAGE_DIMS)
  const [selectedPanelIndex, setSelectedPanelIndex] = useState(null)
  const [linkingPanelIndex, setLinkingPanelIndex] = useState(null)
  const [modifiedSvgUrl, setModifiedSvgUrl] = useState(null)
  const [modifiedSvgText, setModifiedSvgText] = useState(null)
  const [formulaModalOpen, setFormulaModalOpen] = useState(false)
  const [formulaModalIndex, setFormulaModalIndex] = useState(null)
  const [liveExportModalOpen, setLiveExportModalOpen] = useState(false)

  const viewportRef = useRef(null)
  const panelsLoadedRef = useRef(false)
  const saveTimerRef = useRef(null)
  const modifiedBlobUrlRef = useRef(null)
  const linkPanelsRef = useRef([])
  // Cache the raw SVG text so we can rebuild without re-fetching
  const svgTextCacheRef = useRef(null)

  linkPanelsRef.current = linkPanels

  const rebuildSvgPreview = useCallback(async (panels) => {
    if (!canvas?.svgUrl) return
    try {
      let svgText = svgTextCacheRef.current
      if (!svgText) {
        const res = await fetch(canvas.svgUrl)
        if (!res.ok) return
        svgText = await res.text()
        svgTextCacheRef.current = svgText
      }
      const modifiedSvg = applyPanelsToSvgText(svgText, panels)
      const blob = new Blob([modifiedSvg], { type: 'image/svg+xml' })
      if (modifiedBlobUrlRef.current) URL.revokeObjectURL(modifiedBlobUrlRef.current)
      const url = URL.createObjectURL(blob)
      modifiedBlobUrlRef.current = url
      setModifiedSvgUrl(url)
      setModifiedSvgText(modifiedSvg)
    } catch (err) {
      console.warn('Failed to rebuild SVG preview:', err)
    }
  }, [canvas?.svgUrl])

  // Load panels for this canvas from localStorage when design changes
  useEffect(() => {
    if (!canvas) return
    let cancelled = false
    setLoading(true)
    panelsLoadedRef.current = false
    setSelectedPanelIndex(null)
    setLinkingPanelIndex(null)
    svgTextCacheRef.current = null

    if (modifiedBlobUrlRef.current) {
      URL.revokeObjectURL(modifiedBlobUrlRef.current)
      modifiedBlobUrlRef.current = null
    }
    setModifiedSvgUrl(null)
    setModifiedSvgText(null)

    const panels = loadLinkPanelsFromLocal(canvas.pageId)
    setLinkPanels(panels)
    panelsLoadedRef.current = true

    rebuildSvgPreview(panels).finally(() => {
      if (!cancelled) setLoading(false)
    })

    return () => { cancelled = true }
  }, [canvas?.pageId, rebuildSvgPreview])

  useEffect(() => {
    if (!canvas?.pageId) return
    let cancelled = false
    let timerId = 0
    const rafId = requestAnimationFrame(() => {
      timerId = window.setTimeout(async () => {
        if (cancelled || !panelsLoadedRef.current) return
        const panels = linkPanelsRef.current
        if (!panels.length) return
        try {
          const merged = await mergePanelsWithVotingApi(panels, imageDims, currentDateKey)
          if (cancelled || !merged) return
          setLinkPanels(merged)
          setTimeout(() => rebuildSvgPreview(merged), 0)
        } catch (e) {
          console.warn('Could not refresh link panels from vote data:', e)
        }
      }, 0)
    })
    return () => {
      cancelled = true
      cancelAnimationFrame(rafId)
      clearTimeout(timerId)
    }
  }, [
    location.key,
    canvas?.pageId,
    imageDims.width,
    imageDims.height,
    currentDateKey,
    rebuildSvgPreview
  ])

  // Debounced persist to localStorage (per pageId)
  const debouncedSave = useCallback((panels) => {
    if (!panelsLoadedRef.current || !canvas?.pageId) return
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => {
      saveLinkPanelsToLocal(canvas.pageId, panels)
    }, DEBOUNCE_MS)
  }, [canvas?.pageId])

  useEffect(() => {
    debouncedSave(linkPanels)
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    }
  }, [linkPanels, debouncedSave])

  useEffect(() => {
    if (linkingPanelIndex === null) return
    function onKey (e) {
      if (e.key === 'Escape') setLinkingPanelIndex(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [linkingPanelIndex])

  // ── Feature 1: auto-size iframe ───────────────────────────────────────────
  function handlePageDimsDetected ({ width, height }) {
    if (width > 0 && height > 0) {
      setImageDims({ width, height })
    }
  }

  // ── Feature 1: OCR → update panel text → rebuild SVG ─────────────────────
  function handleSelectionCommit (panelIndex) {
    if (!viewportRef.current) return
    setLinkPanels((prev) => {
      const panel = prev[panelIndex]
      if (!panel) return prev
      const sel = panel.selectionsByDate[currentDateKey]
      if (!sel) return prev

      let extractedText = ''
      try {
        extractedText = viewportRef.current.getTextAtSelection(sel) || ''
      } catch (_) {}

      if (!extractedText) return prev

      const updated = [...prev]
      updated[panelIndex] = applyExtractedTextToPanel(panel, extractedText)
      setTimeout(() => rebuildSvgPreview(updated), 0)
      return updated
    })
  }

  // ── Feature 2: panel click → highlight on canvas ─────────────────────────
  function handlePanelClick (panelIndex) {
    setLinkingPanelIndex(null)
    const newIndex = panelIndex === selectedPanelIndex ? null : panelIndex
    setSelectedPanelIndex(newIndex)
  }

  function handleLinkArmToggle (panelIndex) {
    setLinkingPanelIndex((prev) => (prev === panelIndex ? null : panelIndex))
  }

  const handleSvgLinkPick = useCallback((pickedId) => {
    if (linkingPanelIndex === null || !pickedId) return
    const idx = linkingPanelIndex
    setLinkPanels((prev) => {
      const panel = prev[idx]
      if (!panel) return prev
      const current = panel.linkedNodeIds?.[0]
      const nextIds = current === pickedId ? [] : [pickedId]
      const updated = [...prev]
      updated[idx] = { ...panel, linkedNodeIds: nextIds }
      setTimeout(() => rebuildSvgPreview(updated), 0)
      return updated
    })
    setLinkingPanelIndex(null)
  }, [linkingPanelIndex, rebuildSvgPreview])

  // ── Feature 3: open/save formula modal ───────────────────────────────────
  function handleOpenFormula (panelIndex) {
    setFormulaModalIndex(panelIndex)
    setFormulaModalOpen(true)
  }

  function handleCloseFormula () {
    setFormulaModalOpen(false)
    setFormulaModalIndex(null)
  }

  function handleSaveFormula (template, formula) {
    if (formulaModalIndex === null) return
    setLinkPanels((prev) => {
      const updated = [...prev]
      const panel = withoutReasoning({ ...updated[formulaModalIndex] })
      const directText = panel.directText || panel.text || ''
      const computedText = executeFormula(directText, template, formula)
      panel.formula = JSON.stringify({ template, formula })
      panel.directText = directText
      panel.text = computedText
      updated[formulaModalIndex] = panel
      setTimeout(() => rebuildSvgPreview(updated), 0)
      return updated
    })
  }

  // ── Other handlers ────────────────────────────────────────────────────────
  function handleAddLink () {
    let selection = { left: 50, top: 50, width: 100, height: 40 }
    if (viewportRef.current) {
      try {
        const visible = viewportRef.current.getVisibleArea()
        if (visible.width > 0 && visible.height > 0) {
          selection = {
            left: Math.max(0, Math.min(imageDims.width - 100, visible.left + visible.width / 2 - 50)),
            top: Math.max(0, Math.min(imageDims.height - 40, visible.top + visible.height / 2 - 20)),
            width: 100,
            height: 40
          }
        }
      } catch (_) {}
    }
    const color = randomColor()
    setLinkPanels((prev) => [
      ...prev,
      {
        text: '',
        color,
        type: 'direct',
        propertyType: 'none',
        linkedNodeIds: [],
        selectionsByDate: { [currentDateKey]: selection },
        thumbnailsByDate: {},
        enableRetargeting: false
      }
    ])
  }

  async function handleRecommendLinks () {
    if (!canvas) return
    setRecommendationsLoading(true)
    try {
      const importFn = PREGEN_IMPORTS[canvas.pregeneratedKey]
      if (!importFn) return
      const mod = await importFn()
      const data = mod.default || mod
      await new Promise((r) => setTimeout(r, 2000))
      if (data.linkPanels && Array.isArray(data.linkPanels)) {
        const panels = data.linkPanels.map((p) => ({
          text: p.text || '',
          color: p.color || randomColor(),
          type: p.type || 'direct',
          propertyType: p.propertyType || 'none',
          linkedNodeIds: Array.isArray(p.linkedNodeIds) ? p.linkedNodeIds : [],
          selectionsByDate: copySelectionsToTodayKey(p.selectionsByDate || {}, currentDateKey),
          thumbnailsByDate: p.thumbnailsByDate || {},
          enableRetargeting: p.enableRetargeting ?? false,
          reasoning: p.reasoning,
          formula: p.formula,
          directText: p.directText,
          optionalParams: p.optionalParams
        }))
        const panelsWithThresholds = await applyDynamicVoteThresholdsToPanels(panels)
        const mergedPanels = mergePanelsWithViewportText(viewportRef.current, panelsWithThresholds, currentDateKey)
        setLinkingPanelIndex(null)
        setLinkPanels(mergedPanels)
        await rebuildSvgPreview(mergedPanels)
      }
    } catch (e) {
      console.error('Failed to load pregenerated data:', e)
    } finally {
      setRecommendationsLoading(false)
    }
  }

  function handlePropertyTypeChange (panelIndex, propertyType) {
    setLinkPanels((prev) => {
      const updated = prev.map((p, i) =>
        i === panelIndex ? withoutReasoning({ ...p, propertyType }) : p
      )
      setTimeout(() => rebuildSvgPreview(updated), 0)
      return updated
    })
  }

  function handleDeletePanel (panelIndex) {
    if (selectedPanelIndex === panelIndex) setSelectedPanelIndex(null)
    else if (selectedPanelIndex !== null && selectedPanelIndex > panelIndex) {
      setSelectedPanelIndex(selectedPanelIndex - 1)
    }
    if (linkingPanelIndex === panelIndex) setLinkingPanelIndex(null)
    else if (linkingPanelIndex !== null && linkingPanelIndex > panelIndex) {
      setLinkingPanelIndex(linkingPanelIndex - 1)
    }
    setLinkPanels((prev) => prev.filter((_, i) => i !== panelIndex))
  }

  function handleTogglePanelType (panelIndex) {
    setLinkPanels((prev) => {
      const updated = [...prev]
      const nextType = (updated[panelIndex].type === 'direct' || !updated[panelIndex].type)
        ? 'formulaic'
        : 'direct'
      updated[panelIndex] = withoutReasoning({ ...updated[panelIndex], type: nextType })
      return updated
    })
  }

  function handleExportMappingsJson () {
    const payload = { linkPanels }
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const slug = canvas?.pregeneratedKey || canvas?.pageId || canvas?.id || 'mappings'
    a.download = `${slug}-link-mappings.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  function handleResetDesign () {
    if (!canvas) return
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current)
      saveTimerRef.current = null
    }
    clearLinkPanelsLocal(canvas.pageId)
    setSelectedPanelIndex(null)
    setLinkingPanelIndex(null)
    svgTextCacheRef.current = null
    if (modifiedBlobUrlRef.current) {
      URL.revokeObjectURL(modifiedBlobUrlRef.current)
      modifiedBlobUrlRef.current = null
    }
    setModifiedSvgUrl(null)
    setModifiedSvgText(null)
    setLinkPanels([])
    panelsLoadedRef.current = true
    rebuildSvgPreview([])
  }

  async function handleCalibrateSummaryLayout () {
    if (!viewportRef.current?.captureSummaryLayoutCalibration) return
    setCalibratingLayout(true)
    try {
      const result = await viewportRef.current.captureSummaryLayoutCalibration()
      if (result.error) {
        alert(result.error)
        return
      }
      let secret
      if (!ims?.token) {
        secret = window.prompt(
          'Optional: enter SUMMARY_LAYOUT_SECRET if set in your .env (required when not signed in via Adobe shell). Leave empty if you use IMS auth.'
        )
        if (secret === null) return
      }
      await saveSummaryLayoutCalibration(ims, result.calibration, secret || undefined)
      alert(
        `Saved summary layout calibration (${result.calibration.regions.length} regions, viewport ${result.calibration.viewport.width}×${result.calibration.viewport.height}). Exported SVGs will use this until you recalibrate.`
      )
    } catch (e) {
      console.error(e)
      alert(`Calibration save failed: ${e.message || String(e)}`)
    } finally {
      setCalibratingLayout(false)
    }
  }

  async function handleLiveExportConfirm (pollSeconds) {
    if (!canvas) return
    setExporting(true)
    try {
      const res = await fetch(canvas.svgUrl)
      if (!res.ok) throw new Error(`Failed to fetch SVG: ${res.status}`)
      let svgText = await res.text()
      svgText = stripExistingScript(svgText)
      const apiUrl = getSummaryBoxesUrl()
      const panels = buildLiveExportPanels(linkPanels, currentDateKey)
      const script = generateSummaryLiveScript({
        apiUrl,
        pollSeconds,
        viewport: { width: imageDims.width, height: imageDims.height },
        panels
      })
      svgText = injectScriptIntoSVG(svgText, script)
      downloadSVG(svgText, `living-link-${canvas.pageId}.svg`)
      setLiveExportModalOpen(false)
    } catch (e) {
      console.error('Export failed:', e)
      alert('Export failed. See console for details.')
    } finally {
      setExporting(false)
    }
  }

  if (!canvas) {
    return (
      <div className='addon-panel'>
        <div className='addon-loading-overlay'>Select a design to start.</div>
      </div>
    )
  }

  const formulaModalPanel = formulaModalIndex !== null ? linkPanels[formulaModalIndex] : null
  const selectedPanel = selectedPanelIndex !== null ? linkPanels[selectedPanelIndex] : null

  return (
    <>
      <div className='living-link-addon-workspace'>
        <MockExpressChrome
          canvas={canvas}
          modifiedSvgUrl={modifiedSvgUrl}
          modifiedSvgText={modifiedSvgText}
          selectedPanel={selectedPanel}
          isLinking={linkingPanelIndex !== null}
          linkingPanel={linkingPanelIndex !== null ? linkPanels[linkingPanelIndex] : null}
          onSvgLinkPick={handleSvgLinkPick}
        />

        <div className='addon-panel'>
          <div className='addon-panel-header'>
            <div className='addon-panel-title-row'>
              <p className='addon-panel-title'>Project Concurrent</p>
              {/* <button
                type='button'
                className='addon-calibrate-header-btn'
                onClick={handleCalibrateSummaryLayout}
                disabled={calibratingLayout}
                title='Measure Summary page DOM from the preview and save regions to the server. Open Summary in the preview first.'
              >
                {calibratingLayout ? 'Saving…' : 'Calibrate layout'}
              </button> */}
            </div>
            <div className='addon-url-field'>
              <span className='addon-url-label'>URL</span>
              <input
                className='addon-url-input'
                type='text'
                readOnly
                value={autoUrl}
                title={autoUrl}
              />
            </div>
            <EditorViewport
              ref={viewportRef}
              url={autoUrl}
              imageDims={imageDims}
              zoom={zoom}
              setZoom={setZoom}
              linkPanels={linkPanels}
              setLinkPanels={setLinkPanels}
              currentDateKey={currentDateKey}
              onPageDimsDetected={handlePageDimsDetected}
              onSelectionCommit={handleSelectionCommit}
            />
          </div>

          <div className='addon-panel-body'>
            {loading
              ? <div className='addon-loading-overlay'>Loading…</div>
              : (
                <LinkPanelList
                  linkPanels={linkPanels}
                  selectedPanelIndex={selectedPanelIndex}
                  linkingPanelIndex={linkingPanelIndex}
                  currentDateKey={currentDateKey}
                  recommendationsLoading={recommendationsLoading}
                  onPropertyTypeChange={handlePropertyTypeChange}
                  onDeletePanel={handleDeletePanel}
                  onTogglePanelType={handleTogglePanelType}
                  onPanelClick={handlePanelClick}
                  onLinkArmToggle={handleLinkArmToggle}
                  onOpenFormula={handleOpenFormula}
                  onAddLink={handleAddLink}
                  onRecommendLinks={handleRecommendLinks}
                  onExportMappingsJson={handleExportMappingsJson}
                />
                )}
          </div>

          <div className='addon-export-bar'>
            <button
              type='button'
              className='addon-reset-design-btn'
              onClick={handleResetDesign}
              title='Remove all link rows and clear saved state for this design'
            >
              Reset Design
            </button>
            <button
              type='button'
              className='addon-export-btn'
              onClick={() => setLiveExportModalOpen(true)}
              disabled={exporting}
            >
              Export Live SVG
            </button>
          </div>
        </div>
      </div>

      <FormulaModal
        panel={formulaModalPanel}
        isOpen={formulaModalOpen}
        onClose={handleCloseFormula}
        onSave={handleSaveFormula}
      />

      <LiveSvgExportModal
        isOpen={liveExportModalOpen}
        onClose={() => !exporting && setLiveExportModalOpen(false)}
        onConfirm={handleLiveExportConfirm}
        exporting={exporting}
      />
    </>
  )
}

LivingLinkAddon.propTypes = {
  canvas: PropTypes.object,
  ims: PropTypes.object
}

LivingLinkAddon.defaultProps = {
  ims: null
}
